import React, { useEffect, useRef, useState } from 'react';
import { InputSize } from './Input.styles';
import Chevron from 'src/app/icons/Monospaced/Chevron/Chevron';
import { ClickableContainer, DropdownContainer, OptionListContent, OptionsList } from './Dropdown.styles';
import Typography, { TypographyProps } from 'atoms/Typography/Typography';
import { RadiusPropKeys } from 'styles/spacing';
import Stack from 'atoms/Layout/Stack/Stack';
import Clickable from 'atoms/Clickable/Clickable';
import { cssVar } from 'styles/helper';
import useWindowResize from 'src/hooks/useWindowResize';

export interface DropdownOption {
  label: string;
  onClick: () => void;
}

export interface DropdownProps {
  size?: InputSize;
  onPlaceholderClick?: () => void;
  typographyProps?: Omit<TypographyProps, 'text'>;
  placeholder?: string;
  className?: string;
  borderRadius?:
    | RadiusPropKeys
    | {
        topRight?: RadiusPropKeys;
        bottomRight?: RadiusPropKeys;
        bottomLeft?: RadiusPropKeys;
        topLeft?: RadiusPropKeys;
      };
  options: DropdownOption[];
}

const Dropdown = ({
  size = 'md',
  typographyProps,
  placeholder,
  className,
  options = [],
  borderRadius,
  onPlaceholderClick,
}: DropdownProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const optionListRef = useRef<HTMLDivElement>(null);

  const { width } = useWindowResize();

  useEffect(() => {
    if (!optionListRef.current) return;

    if (isOpen) {
      optionListRef.current.style.maxHeight = optionListRef.current.scrollHeight + 10 + 'px'; // Added 10px for padding compensation
      optionListRef.current.style.opacity = '1';
    } else {
      optionListRef.current.style.maxHeight = '0px';
      optionListRef.current.style.opacity = '0';
    }
  }, [isOpen, width]);

  return (
    <ClickableContainer onClick={onPlaceholderClick}>
      <DropdownContainer
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className={className}
        $size={size}
        $borderRadius={borderRadius}
      >
        <Typography
          size='sm'
          textAlign='left'
          fontWeight='font-weight-semibold'
          type='paragraph'
          {...typographyProps}
        >
          {placeholder}
        </Typography>
        <Stack
          justify='center'
          align='center'
        >
          <Chevron
            orientation={'down'}
            width={20}
            color='var(--gray-light-400)'
          />
        </Stack>
        <OptionsList
          ref={optionListRef}
          position='absolute'
        >
          <OptionListContent
            background='bg-primary'
            border={`1px solid ${cssVar('border-secondary-alt')}`}
            borderRadius='radius-md'
            marginTop='spacing-sm'
          >
            <Stack
              direction='column'
              padding={['spacing-xs', 'spacing-sm']}
            >
              {options.map((option) => (
                <Clickable
                  onClick={(e) => {
                    e.stopPropagation();

                    option.onClick();
                  }}
                  key={option.label}
                  className='dropdown-option'
                >
                  <Stack
                    justify='flex-start'
                    align='center'
                    padding='spacing-md'
                  >
                    <Typography
                      size='sm'
                      textAlign='left'
                      fontWeight='font-weight-semibold'
                      type='paragraph'
                      whiteSpace='nowrap'
                    >
                      {option.label}
                    </Typography>
                  </Stack>
                </Clickable>
              ))}
            </Stack>
          </OptionListContent>
        </OptionsList>
      </DropdownContainer>
    </ClickableContainer>
  );
};

export default Dropdown;
